/**
 * /admin/feedback — N9 — thread list + side drawer with reply form.
 */
import { useEffect, useRef, useState } from "react";
import useDialogDismiss from "../../hooks/useDialogDismiss";
import {
  RefreshCw, Loader2, X, Send, CheckCircle2, AlertTriangle, Check,
} from "lucide-react";
import {
  listFeedbackThreads, getFeedbackThread, replyFeedbackThread,
  markFeedbackResolved,
} from "../../api/admin.extras.api";

const STATUS_OPTIONS = [
  { value: "",            label: "All statuses" },
  { value: "OPEN",        label: "Open" },
  { value: "ACKED",       label: "Acknowledged" },
  { value: "IN_PROGRESS", label: "In progress" },
  { value: "RESOLVED",    label: "Resolved" },
];

const STATUS_STYLES = {
  OPEN: "bg-amber-100 text-amber-700",
  ACKED: "bg-blue-100 text-blue-700",
  IN_PROGRESS: "bg-purple-100 text-purple-700",
  RESOLVED: "bg-green-100 text-green-700",
};

const SEVERITY_STYLES = {
  1: "bg-green-100 text-green-700",
  2: "bg-lime-100 text-lime-700",
  3: "bg-gray-100 text-gray-700",
  4: "bg-orange-100 text-orange-700",
  5: "bg-red-100 text-red-700",
};

export default function AdminFeedback() {
  const [rows, setRows] = useState([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("");
  const [sevFilter, setSevFilter] = useState("");
  const [selectedId, setSelectedId] = useState(null);
  const [markingId, setMarkingId] = useState(null);

  // One-click "completed" without opening the drawer or writing a
  // reply. Used when the operator handled the issue offline (Slack /
  // WhatsApp / phone) and just needs the thread off the open list.
  async function markComplete(threadId, e) {
    if (e) e.stopPropagation();  // don't bubble to row click → drawer
    if (!confirm("Mark this feedback as completed? Closes the thread without sending a reply.")) return;
    setMarkingId(threadId);
    try {
      await markFeedbackResolved(threadId);
      await load();
    } catch (err) {
      alert(err?.response?.data?.detail || err.message || "Couldn't mark complete.");
    } finally {
      setMarkingId(null);
    }
  }

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [statusFilter, sevFilter]);

  async function load() {
    setLoading(true);
    try {
      const { data } = await listFeedbackThreads({
        status: statusFilter || undefined,
        severity: sevFilter || undefined,
      });
      setRows(data.results || []);
      setCount(data.count || 0);
    } finally { setLoading(false); }
  }

  return (
    <div className="p-6 space-y-4">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Feedback</h1>
          <p className="text-sm text-text-secondary mt-1">
            {count.toLocaleString("en-IN")} thread(s). Click a row to reply.
          </p>
        </div>
        <button onClick={load} className="px-3 py-2 text-sm rounded-md border border-border-default hover:bg-bg-secondary flex items-center gap-1.5">
          <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Refresh
        </button>
      </header>

      <div className="flex items-center gap-3 text-sm">
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="px-3 py-1.5 rounded-md border border-border-default bg-bg-primary">
          {STATUS_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        <select value={sevFilter} onChange={(e) => setSevFilter(e.target.value)} className="px-3 py-1.5 rounded-md border border-border-default bg-bg-primary">
          <option value="">All severities</option>
          <option value="4">Severity ≥ 4 (urgent)</option>
          <option value="5">Severity = 5 (critical)</option>
        </select>
      </div>

      <div className="rounded-md border border-border-default bg-bg-primary overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-bg-secondary text-left text-xs uppercase tracking-wider text-text-secondary">
            <tr>
              <th className="px-4 py-2.5">User</th>
              <th className="px-4 py-2.5">Classification</th>
              <th className="px-4 py-2.5">Sev</th>
              <th className="px-4 py-2.5">Status</th>
              <th className="px-4 py-2.5">Summary</th>
              <th className="px-4 py-2.5 text-right">Msgs</th>
              <th className="px-4 py-2.5">Created</th>
              <th className="px-4 py-2.5 text-right">Action</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border-default">
            {loading && rows.length === 0 && (
              <tr><td colSpan={8} className="px-4 py-8 text-center text-text-secondary">
                <Loader2 className="w-5 h-5 inline-block mr-2 animate-spin" /> Loading…
              </td></tr>
            )}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={8} className="px-4 py-8 text-center text-text-secondary">No feedback threads.</td></tr>
            )}
            {rows.map((t) => (
              <tr key={t.id} className="hover:bg-bg-secondary cursor-pointer" onClick={() => setSelectedId(t.id)}>
                <td className="px-4 py-2 text-xs">{t.user_email || "(anon)"}</td>
                <td className="px-4 py-2 text-xs">{t.classification}</td>
                <td className="px-4 py-2">
                  <span className={`px-2 py-0.5 rounded text-xs font-semibold ${SEVERITY_STYLES[t.severity] || ""}`}>{t.severity}</span>
                </td>
                <td className="px-4 py-2">
                  <span className={`px-2 py-0.5 rounded text-xs font-medium ${STATUS_STYLES[t.status] || ""}`}>{t.status}</span>
                </td>
                <td className="px-4 py-2 max-w-md truncate text-text-secondary" title={t.summary}>{t.summary || "—"}</td>
                <td className="px-4 py-2 text-right">{t.message_count}</td>
                <td className="px-4 py-2 text-xs text-text-secondary">
                  {new Date(t.created_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short" })}
                </td>
                <td className="px-4 py-2 text-right">
                  {t.status === "RESOLVED" ? (
                    <span className="inline-flex items-center gap-1 text-[11px] text-emerald-700">
                      <CheckCircle2 className="w-3.5 h-3.5" /> Done
                    </span>
                  ) : (
                    <button
                      type="button"
                      onClick={(e) => markComplete(t.id, e)}
                      disabled={markingId === t.id}
                      title="Mark this feedback as completed without sending a reply"
                      className="inline-flex items-center gap-1 px-2 py-1 text-[11px] rounded border border-emerald-200 text-emerald-700 bg-emerald-50 hover:bg-emerald-100 disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      {markingId === t.id
                        ? <Loader2 className="w-3 h-3 animate-spin" />
                        : <Check className="w-3 h-3" />}
                      Mark complete
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {selectedId && (
        <ThreadDrawer
          id={selectedId}
          onClose={() => setSelectedId(null)}
          onReplied={() => { setSelectedId(null); load(); }}
        />
      )}
    </div>
  );
}

function ThreadDrawer({ id, onClose, onReplied }) {
  const [thread, setThread] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reply, setReply] = useState("");
  const [newStatus, setNewStatus] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const panelRef = useRef(null);
  useDialogDismiss({ open: true, onClose, ref: panelRef });

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  async function load() {
    setLoading(true);
    setError("");
    try {
      const { data } = await getFeedbackThread(id);
      setThread(data);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || "Couldn't load thread.");
    } finally { setLoading(false); }
  }

  async function send(e) {
    e.preventDefault();
    if (!reply.trim()) return;
    setSending(true); setError("");
    try {
      await replyFeedbackThread(id, { message: reply, status: newStatus });
      onReplied();
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || "Reply failed.");
    } finally { setSending(false); }
  }

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex justify-end" role="presentation" onClick={onClose}>
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={`Feedback thread ${id}`}
        className="bg-bg-primary w-full max-w-xl h-full overflow-y-auto p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold">Thread #{id}</h2>
          <button onClick={onClose} className="text-text-secondary hover:text-text-primary">
            <X className="w-5 h-5" />
          </button>
        </div>

        {loading && <div className="text-text-secondary"><Loader2 className="w-5 h-5 inline-block mr-2 animate-spin" /> Loading…</div>}
        {!loading && !thread && error && (
          <div
            role="alert"
            className="rounded-md border border-red-500/40 bg-red-500/10 text-red-300 p-3 text-sm flex items-center justify-between gap-2"
          >
            <span className="flex items-center gap-2 min-w-0">
              <AlertTriangle className="w-4 h-4 shrink-0" />
              <span className="truncate">{error}</span>
            </span>
            <button
              onClick={load}
              className="shrink-0 text-xs px-2 py-1 rounded border border-red-500/60 hover:bg-red-500/20"
            >
              Retry
            </button>
          </div>
        )}
        {thread && (
          <>
            <div className="text-sm text-text-secondary mb-4 space-y-1">
              <div>From: <strong className="text-text-primary">{thread.user_email || "(anonymous)"}</strong></div>
              {thread.user_phone && <div>Phone: {thread.user_phone}</div>}
              <div>Status: <span className={`px-2 py-0.5 rounded text-xs ${STATUS_STYLES[thread.status] || ""}`}>{thread.status}</span>
                {" · "}Severity: <span className={`px-2 py-0.5 rounded text-xs ${SEVERITY_STYLES[thread.severity] || ""}`}>{thread.severity}</span>
                {" · "}<code className="text-xs">{thread.classification}</code></div>
              {thread.page_path && <div>Page: <code className="text-xs">{thread.page_path}</code></div>}
            </div>

            <div className="space-y-3 mb-6 max-h-80 overflow-y-auto">
              {thread.messages.map((m) => {
                const role = (m.role || "").toLowerCase();
                // Translucent tints + accent border keep the bubble
                // visible against the dark drawer bg while the
                // text-text-primary colour stays fully readable in
                // any theme. The previous bg-purple-50 / bg-blue-50
                // tinted near-white in dark mode → invisible body
                // text was the bug shown in change12.jpg.
                const bubble =
                  role === "user"  ? "bg-bg-secondary border border-border-default"
                  : role === "bot" ? "bg-purple-500/10 border border-purple-500/30"
                  :                  "bg-blue-500/10 border border-blue-500/30";
                const label =
                  role === "user"  ? "text-text-secondary"
                  : role === "bot" ? "text-purple-400"
                  :                  "text-blue-400";
                return (
                  <div key={m.id} className={`rounded-md p-3 text-sm ${bubble}`}>
                    <div className={`text-[10px] uppercase tracking-wider mb-1 font-medium ${label}`}>
                      {m.role} · {new Date(m.created_at).toLocaleString("en-IN")}
                    </div>
                    <div className="whitespace-pre-wrap text-text-primary">{m.content}</div>
                  </div>
                );
              })}
            </div>

            <form onSubmit={send} className="space-y-3 border-t border-border-default pt-4">
              <h3 className="text-sm font-semibold">Reply</h3>
              <textarea
                value={reply} onChange={(e) => setReply(e.target.value)}
                rows={4} required maxLength={4000}
                placeholder="Your reply (emailed + WhatsApped to the user)…"
                className="w-full px-3 py-2 text-sm rounded-md border border-border-default bg-bg-primary"
              />
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <label className="text-sm flex items-center gap-2">
                  Promote status:
                  <select value={newStatus} onChange={(e) => setNewStatus(e.target.value)} className="px-2 py-1 rounded border border-border-default text-xs">
                    <option value="">— just reply</option>
                    <option value="IN_PROGRESS">In progress</option>
                    <option value="RESOLVED">Resolved (closes thread)</option>
                  </select>
                </label>
                <button type="submit" disabled={sending} className="px-3 py-2 text-sm rounded-md bg-button-primary text-white hover:opacity-90 flex items-center gap-1.5 disabled:opacity-50">
                  {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  Send reply
                </button>
              </div>
              {error && <div className="text-xs text-rose-300"><AlertTriangle className="w-3.5 h-3.5 inline-block mr-1" />{error}</div>}
            </form>
          </>
        )}
      </div>
    </div>
  );
}
